import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseTooltipTouchOptions {
  isControlled: boolean
  controlledOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

interface UseTooltipTouchReturn {
  isTouchDevice: boolean
  isOpen: boolean
  handleOpenChange: (open: boolean) => void
  handleTouchInteraction: (e: React.TouchEvent | React.MouseEvent) => void
}

export function useTooltipTouch({
  isControlled,
  controlledOpen,
  onOpenChange
}: UseTooltipTouchOptions): UseTooltipTouchReturn {
  const [internalOpen, setInternalOpen] = useState(false)

  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false
    
    // Check for touch capability
    const hasTouchSupport = 'ontouchstart' in window || (navigator.maxTouchPoints > 0)
    
    // For hybrid devices, also check if it's primarily touch-based
    // by examining screen size and pointer capabilities
    if (hasTouchSupport) {
      const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
      const isMobileSize = window.matchMedia('(max-width: 768px)').matches
      
      return hasCoarsePointer || isMobileSize
    }
    
    return hasTouchSupport
  }, [])

  const isOpen = isControlled ? Boolean(controlledOpen) : internalOpen
  const handleOpenChange = useCallback((open: boolean) => {
    if (isControlled) {
      onOpenChange?.(open)
    } else {
      setInternalOpen(open)
      onOpenChange?.(open)
    }
  }, [isControlled, onOpenChange])

  // Handle click outside for touch devices
  useEffect(() => {
    if (!isTouchDevice || !isOpen) return

    const handleGlobalClick = (e: Event) => {
      const target = e.target as Element
      if (!target.closest('[data-tooltip-trigger]') && !target.closest('[data-radix-tooltip-content]')) {
        handleOpenChange(false)
      }
    }
    
    document.addEventListener('click', handleGlobalClick, true)
    return () => {
      document.removeEventListener('click', handleGlobalClick, true)
    }
  }, [isTouchDevice, isOpen, handleOpenChange])

  const handleTouchInteraction = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (isTouchDevice && 'touches' in e) {
      e.preventDefault()
    }
    handleOpenChange(!isOpen)
  }, [isTouchDevice, isOpen, handleOpenChange])

  return {
    isTouchDevice,
    isOpen,
    handleOpenChange,
    handleTouchInteraction
  }
}